import "./navbar.scss";
import { Link, useNavigate } from 'react-router-dom';
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonIcon from '@mui/icons-material/Person';
import { useContext, useState, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import UserDropdown from "../userDropdown/UserDropdown";
import { useSidebarState } from '../sidebar/SidebarStateContext';
import { toggleSidebar } from '../sidebar/sidebarActions';
import { db } from "../../firebase";
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  orderBy,
  getDocs
} from "firebase/firestore";
import BusinessIcon from '@mui/icons-material/Business';
import "./notificationBubble/notificationBubble.scss";

/**
 * Componente de la barra de navegación superior.
 */
const Navbar = () => {
  const { currentUser, adminData, currentService } = useContext(AuthContext);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadFormNotifications, setUnreadFormNotifications] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const navigate = useNavigate();

  const { dispatch: sidebarDispatch } = useSidebarState();

  // Efecto para cargar notificaciones del dashboard sin leer
  useEffect(() => {
    if (!currentUser?.email || !currentService?.id) return;

    // Obtener referencia a las notificaciones del dashboard
    const notificationsRef = collection(db, "servicios", currentService.id, "notificaciones_dashboard");
    
    // Consulta para obtener notificaciones no leídas
    const q = query(
      notificationsRef,
      where("destinatario", "in", [currentUser.email, "todos"]),
      where("leida", "==", false),
      orderBy("fecha", "desc")
    );

    // Suscribirse a los cambios en tiempo real
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setUnreadNotifications(snapshot.docs.length);
    });

    return () => unsubscribe();
  }, [currentUser, currentService]);

  // Efecto para cargar notificaciones de formularios sin leer
  useEffect(() => {
    if (!currentUser?.email || !currentService?.id) return;

    // Obtener referencia a las notificaciones de formularios
    const formNotificationsRef = collection(db, "servicios", currentService.id, "notificaciones_formularios");
    
    // Consulta para obtener notificaciones no leídas
    const q = query(
      formNotificationsRef,
      where("destinatario", "in", [currentUser.email, "todos"]),
      where("leida", "==", false)
    );

    // Suscribirse a los cambios en tiempo real
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setUnreadFormNotifications(snapshot.docs.length);
    });

    return () => unsubscribe();
  }, [currentUser, currentService]);

  // Efecto para cargar empresas
  useEffect(() => {
    if (!currentService?.id) return;
    
    const fetchCompanies = async () => {
      setIsLoadingCompanies(true);
      try {
        const companiesRef = collection(db, 'servicios', currentService.id, 'empresas');
        const companiesSnap = await getDocs(companiesRef);
        const companiesData = companiesSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCompanies(companiesData);
        
        // Si hay empresas y no hay una seleccionada, seleccionar la primera
        if (companiesData.length > 0 && !selectedCompany) {
          setSelectedCompany(companiesData[0].id);
        }
      } catch (error) {
        console.error("Error al cargar empresas:", error);
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, [currentService, selectedCompany]);

  const handleUserClick = () => {
    setIsDropdownOpen(!isDropdownOpen); // Cambia el estado del modal
  };

  const handleToggleSidebar = () => {
    sidebarDispatch(toggleSidebar());
  };

  const handleNotificationsClick = () => {
    navigate('/notificaciones-internas');
  };

  const handleFormNotificationsClick = () => {
    navigate('/notificaciones-internas');
  };

  const handleCompanyChange = (event) => {
    setSelectedCompany(event.target.value);
  };

  const getSelectedCompanyData = () => {
    return companies.find(company => company.id === selectedCompany);
  };

  return (
    <div className="navbar">
      <div className="wrapper">
        {/* Hamburger menu on the left */}
        <div className="item" onClick={handleToggleSidebar}>
          <MenuIcon className="iconMenu" />
        </div>
        <div className="items">
          {/* Selector de empresa */}
          <div className="item company-selector">
            <BusinessIcon className="icon" />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <select
                value={selectedCompany}
                onChange={handleCompanyChange}
                disabled={isLoadingCompanies}
                style={{
                  backgroundColor: 'transparent',
                  color: '#2c3e50',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  minWidth: '200px',
                  outline: 'none',
                  cursor: 'pointer',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  appearance: 'none'
                }}
              >
                {companies.map((company) => (
                  <option 
                    key={company.id} 
                    value={company.id}
                    style={{
                      backgroundColor: 'white',
                      color: '#2c3e50',
                      fontWeight: 'bold',
                      padding: '8px'
                    }}
                  >
                    {company.nombre}
                  </option>
                ))}
              </select>
              {getSelectedCompanyData() && (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#7f8c8d', 
                  marginTop: '2px',
                  fontWeight: 'normal'
                }}>
                  {getSelectedCompanyData().codEmpresa} - {getSelectedCompanyData().cif}
                </div>
              )}
            </div>
          </div>

          <div className="item" onClick={handleNotificationsClick}>
            <NotificationsNoneOutlinedIcon className="icon" />
            {unreadNotifications > 0 && (
              <div className="counter">
                {unreadNotifications > 99 ? "99+" : unreadNotifications}
              </div>
            )}
          </div>
          <div className="item" onClick={handleFormNotificationsClick}>
            <MailOutlineIcon className="icon" />
            {unreadFormNotifications > 0 && (
              <div className="counter2">
                {unreadFormNotifications > 99 ? "99+" : unreadFormNotifications}
              </div>
            )}
          </div>
          <div className="item">
            {adminData && adminData.foto ? (
              <img src={adminData.foto} alt="Admin" className="avatar" />
            ) : (
              <PersonIcon className="avatar" />
            )}
            <p className="user" onClick={handleUserClick}>{currentUser?.email || 'Usuario'} &#709;</p>
          </div>
          {isDropdownOpen && <UserDropdown onClose={() => setIsDropdownOpen(false)} />}
        </div>
      </div>
    </div>
  );
};

export default Navbar;